import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { BillingType } from './models';
import { baseApi } from '@centerpiece/components';

export const billing_type = baseApi
  .enhanceEndpoints({ addTagTypes: ['BillingTypes', 'BillingType'] })
  .injectEndpoints({
    endpoints: builder => ({
      getBillingTypes: builder.query<BillingType[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'billing_type',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'BillingType' as const,
                  id,
                })),
                'BillingTypes',
              ]
            : ['BillingTypes'],
      }),
      getBillingType: builder.query<BillingType, string>({
        query: id => ({
          url: `billing_type/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'BillingType', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createBillingType: builder.mutation({
        query: request => ({
          url: 'billing_type',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['BillingTypes'],
      }),
      updateBillingType: builder.mutation<BillingType, BillingType>({
        query: request => ({
          url: `billing_type/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'BillingType', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            billing_type.util.updateQueryData(
              'getBillingType',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(billing_type.util.invalidateTags(['BillingTypes']))
             */
            dispatch(billing_type.util.invalidateTags(['BillingTypes']));
          }
        },
      }),
      deleteBillingType: builder.mutation<BillingType, number>({
        query: id => ({
          url: `billing_type/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'BillingType' as const, arg }, 'BillingTypes']
            : ['BillingTypes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetBillingTypesQuery,
  useGetBillingTypeQuery,
  useCreateBillingTypeMutation,
  useUpdateBillingTypeMutation,
  useDeleteBillingTypeMutation,
  usePrefetch: useBillingTypesPrefetch,
} = billing_type;
const selectBillingTypesRoot = billing_type.endpoints.getBillingTypes.select(
  {}
);
export const selectBillingTypes = createSelector(
  selectBillingTypesRoot,
  billing_type => billing_type.data || []
);
